import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Nav from "./Nav";
import {
  MapPin,
  Phone,
  Utensils,
  Mail,
  Clock,
  Car,
  Baby,
  CreditCard,
  Toilet,
  IceCream,
  Candy,
  Droplets,
  WashingMachine,
  Package,
  Ticket,
  ShoppingBag,
  ArrowLeft,
  Navigation,
  CheckCircle2,
  Store,
  CalendarDays,
  Sparkles,
  GlassWater,
} from "lucide-react";

import "./StoreDetails.css";

import BP from "../images/bp-logo.webp";
import Evri from "../images/Evri-logo.webp";
import Hoagies from "../images/HoagiesLogo-small.webp";
import Lottery from "../images/National-Lottery-Logo.webp";
import PayPoint from "../images/paypoint-logo.webp";
import Gulf from "../images/gulf-logo.webp";
import Subway from "../images/subway.webp";
import TubbeesLogo from "../images/Tubbees-logo.webp";
import V4u from "../images/v4uLogo.webp";
import Wish from "../images/wish-logo.webp";
import CoOpLogo from "../images/co-op-logo.png";
import PremierLogo from "../images/Premier-Logo.jpg";
// Helper for open
const DAY_INDEX = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const isDayInRange = (day, range) => {
  const days = range.split("-").map((item) => item.trim().toUpperCase());

  if (days.length === 1) {
    return DAY_INDEX[days[0]] === day;
  }

  const start = DAY_INDEX[days[0]];
  const end = DAY_INDEX[days[1]];

  if (start === undefined || end === undefined) {
    return false;
  }

  if (start <= end) {
    return day >= start && day <= end;
  }

  return day >= start || day <= end;
};

const getStoreStatus = (businessHours, now = new Date()) => {
  const hours = businessHours?.hours || {};

  const currentDay = now.getDay();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const matchingEntry = Object.entries(hours).find(([dayRange]) =>
    isDayInRange(currentDay, dayRange),
  );

  if (!matchingEntry) {
    return {
      isOpen: false,
      label: "Closed Now",
    };
  }

  const [, timeRange] = matchingEntry;

  if (timeRange === "24/7") {
    return {
      isOpen: true,
      label: "Open Now",
    };
  }

  const times = timeRange.split("-").map((time) => time.trim());

  if (times.length !== 2) {
    return {
      isOpen: false,
      label: "Closed Now",
    };
  }

  const [openHour, openMinute] = times[0].split(":").map(Number);

  const [closeHour, closeMinute] = times[1].split(":").map(Number);

  const openingMinutes = openHour * 60 + openMinute;
  const closingMinutes = closeHour * 60 + closeMinute;

  let isOpen;

  if (closingMinutes <= openingMinutes) {
    isOpen =
      currentMinutes >= openingMinutes || currentMinutes < closingMinutes;
  } else {
    isOpen =
      currentMinutes >= openingMinutes && currentMinutes < closingMinutes;
  }

  return {
    isOpen,
    label: isOpen ? "Open Now" : "Closed Now",
  };
};
export default function StoreDetails() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const brandImages = {
    Vaped4U: V4u,
    Tubbees: TubbeesLogo,
    Hoagies: Hoagies,
    "Co-Op": CoOpLogo,
    "Co-op": CoOpLogo,
    Premier: PremierLogo,
    Subway: Subway,
    BP: BP,
    "BP Fuel": BP,
    Evri: Evri,
    Wish: Wish,
    "National Lottery": Lottery,
    Lottery: Lottery,
    PayPoint: PayPoint,
    Gulf: Gulf,
  };

  useEffect(() => {
    const fetchStore = async () => {
      try {
        setLoading(true);
        setError(false);

        const res = await axios.get(
          "https://maps.rt-itservices.co.uk?site=RaceTrack",
        );

        const locations = res.data?.sites || [];

        const selectedStore = locations.find(
          (location) =>
            location.name?.toLowerCase() ===
            decodeURIComponent(name).toLowerCase(),
        );

        if (!selectedStore) {
          setStore(null);
          setError(true);
        } else {
          setStore(selectedStore);
        }
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error(err);
        }

        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchStore();
  }, [name]);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);
  if (loading) {
    return (
      <div className="store-details-page">
        <div className="store-loading">
          <div className="store-spinner"></div>
          <div className="store-loading-text">Loading store...</div>
        </div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="store-details-page">
        <div className="store-not-found">
          <Store size={54} />

          <div className="store-not-found-title">Store not found</div>

          <div className="store-not-found-text">
            We couldn't find the location you're looking for.
          </div>

          <button onClick={() => navigate("/locations")}>
            <ArrowLeft size={18} />
            Back to locations
          </button>
        </div>
      </div>
    );
  }

  const address = store.Address || {};
  const hours = store.BusinessHours || {};
  const openingHours = hours.hours || {};
  const utilities = store.utilities || {};
  const brands = Array.isArray(store.Brands) ? store.Brands : [];

  const storeStatus = getStoreStatus(store.BusinessHours, currentTime);

  const latitude = Number(address.latitude);
  const longitude = Number(address.longitude);

  const fullAddress = [
    address.addressLine1,
    address.addressLine2,
    address.addressLine3,
    address.postcode,
  ]
    .filter(Boolean)
    .join(", ");

  const mapsUrl =
    Number.isFinite(latitude) && Number.isFinite(longitude)
      ? `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`
      : null;

  const facilityItems = [
    {
      key: "bp",
      label: "Fuel",
      icon: Droplets,
      image: BP,
      active: utilities.bp,
    },
    {
      key: "gulf",
      label: "Gulf Fuel",
      icon: Droplets,
      image: Gulf,
      active: utilities.gulf,
    },
    {
      key: "hoagies",
      label: "Hoagies",
      icon: Store,
      image: Hoagies,
      active: utilities.hoagies,
    },
    {
      key: "subway",
      label: "Subway",
      icon: Store,
      image: Subway,
      active: utilities.subway,
    },
    {
      key: "tubbees",
      label: "Tubbees",
      icon: IceCream,
      image: TubbeesLogo,
      active:
        utilities.tubbeesDessert === true || utilities.tubbeesSlushee === true,
    },
    {
      key: "hermes",
      label: "Evri",
      icon: Package,
      image: Evri,
      active: utilities.hermes,
    },
    {
      key: "wish",
      label: "Wish",
      icon: ShoppingBag,
      image: Wish,
      active: utilities.wish,
    },
    {
      key: "lottery",
      label: "Lottery",
      icon: Ticket,
      image: Lottery,
      active: utilities.lottery,
    },
    {
      key: "paypoint",
      label: "PayPoint",
      icon: CreditCard,
      image: PayPoint,
      active: utilities.paypoint,
    },
    {
      key: "v4u",
      label: "Vaped4U",
      icon: ShoppingBag,
      image: V4u,
      active: utilities.v4u,
    },
    {
      key: "premier",
      label: "Premier",
      icon: ShoppingBag,
      image: PremierLogo,
      active: utilities.premier === true || utilities.premier === "true",
    },
    {
      key: "co_op",
      label: "Co-op",
      icon: ShoppingBag,
      image: CoOpLogo,
      active: utilities.co_op,
    },

    // Services without an available image
    {
      key: "parking",
      label: "Parking",
      icon: Car,
      active: utilities.parking,
    },
    {
      key: "babyChange",
      label: "Baby changing",
      icon: Baby,
      active: utilities.babyChange,
    },
    {
      key: "atm",
      label: "ATM",
      icon: CreditCard,
      active: utilities.atm,
    },
    {
      key: "toilets",
      label: "Toilets",
      icon: Toilet,
      active: utilities.toilets,
    },
    {
      key: "hasSlush",
      label: "Slush",
      icon: GlassWater,
      active: utilities.hasSlush,
    },
    {
      key: "hasDesserts",
      label: "Desserts",
      icon: IceCream,
      active: utilities.hasDesserts,
    },
    {
      key: "hasIceCream",
      label: "Ice cream",
      icon: IceCream,
      active: utilities.hasIceCream,
    },
    {
      key: "hasCandy",
      label: "Candy",
      icon: Candy,
      active: utilities.hasCandy,
    },
    {
      key: "jetWash",
      label: "Jet wash",
      icon: Droplets,
      active: utilities.jetWash,
    },
    {
      key: "laundry",
      label: "Laundry",
      icon: WashingMachine,
      active: utilities.laundry,
    },
    {
      key: "PerfectDraftRewards",
      label: "PerfectDraft Rewards",
      icon: GlassWater,
      active: utilities.PerfectDraftRewards,
    },
  ];

  const activeFacilities = facilityItems.filter((facility) => facility.active);

  return (
    <>
      <Nav />

      <div className="store-details-page">
        {/* HERO */}
        <section className="store-hero">
          <div className="store-hero-pattern"></div>
          <div className="store-hero-overlay"></div>

          <div className="store-hero-content">
            <div className="topButton">
              <button className="back-button" onClick={() => navigate(-1)}>
                <ArrowLeft size={18} />
                Back
              </button>

              <div className="hero-badge">
                <Store size={15} />
                RACETRACK LOCATION
              </div>
            </div>

            <div className="store-hero-title">{store.name}</div>

            <div className="hero-address">
              <MapPin size={20} />
              <span>{fullAddress}</span>
            </div>

            <div className="hero-actions">
              {address.phoneNumber && (
                <a
                  href={`tel:${address.phoneNumber}`}
                  className="hero-button hero-button-red"
                >
                  <Phone size={18} />
                  Call store
                </a>
              )}

              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hero-button hero-button-white"
                >
                  <Navigation size={18} />
                  Get directions
                </a>
              )}
            </div>
          </div>
        </section>

        <main className="store-details-container">
          {/* QUICK INFO */}
          <section className="quick-info-grid">
            <div className="quick-card">
              <div className="quick-icon">
                <Clock size={22} />
              </div>

              <div className="quick-card-content">
                <div className="quick-label">Are We Open?</div>

                <div
                  className={`quick-value ${
                    storeStatus.isOpen ? "store-open" : "store-closed"
                  }`}
                >
                  {storeStatus.label}
                </div>
              </div>
            </div>

            <div className="quick-card">
              <div className="quick-icon">
                <Phone size={22} />
              </div>

              <div className="quick-card-content">
                <div className="quick-label">Phone</div>

                <div className="quick-value">
                  {address.phoneNumber || "Not available"}
                </div>
              </div>
            </div>
            <div className="quick-card">
              <div className="quick-icon">
                <Utensils size={22} />
              </div>

              <div className="quick-card-content">
                <div className="quick-label">Dining</div>

                <div className="quick-value">
                  {store.sitIn && store.takeaway
                    ? "Sit-in & takeaway"
                    : store.sitIn
                      ? "Sit-in available"
                      : store.takeaway
                        ? "Takeaway available"
                        : "Not specified"}
                </div>
              </div>
            </div>
          </section>

          <div className="store-content-grid">
            <div className="store-main-column">
              {/* OPENING HOURS */}
              <section className="store-section">
                <div className="section-heading">
                  <div className="section-icon">
                    <Clock size={21} />
                  </div>

                  <div>
                    <div className="section-title">Opening hours</div>

                    <div className="section-subtitle">Plan your visit</div>
                  </div>
                </div>

                <div className="hours-card">
                  {Object.entries(openingHours).length > 0 ? (
                    Object.entries(openingHours).map(([days, time]) => (
                      <div className="hours-row" key={days}>
                        <div className="hours-day">
                          <CalendarDays size={18} />
                          <span>{days}</span>
                        </div>

                        <div className="hours-value">{time}</div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-card">
                      Opening hours are not available for this location.
                    </div>
                  )}
                </div>
              </section>

              {/* FACILITIES */}
              <section className="store-section">
                <div className="section-heading">
                  <div className="section-icon">
                    <Sparkles size={21} />
                  </div>

                  <div>
                    <div className="section-title">What's available</div>

                    <div className="section-subtitle">
                      Facilities and services at this location
                    </div>
                  </div>
                </div>

                {activeFacilities.length > 0 ? (
                  <div className="facilities-grid">
                    {activeFacilities.map((facility) => {
                      const Icon = facility.icon;

                      return (
                        <div className="facility-card" key={facility.key}>
                          <div className="facility-icon">
                            {facility.image ? (
                              <img src={facility.image} alt={facility.label} />
                            ) : (
                              <Icon size={21} />
                            )}
                          </div>

                          <div className="facility-name">{facility.label}</div>

                          <CheckCircle2 className="facility-check" size={17} />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-card">
                    No facilities have been listed for this location.
                  </div>
                )}
              </section>

              {/* BRANDS */}
              {brands.length > 0 && (
                <section className="store-section">
                  <div className="section-heading">
                    <div className="section-icon">
                      <Store size={21} />
                    </div>

                    <div>
                      <div className="section-title">At this location</div>

                      <div className="section-subtitle">
                        Brands and services available in store
                      </div>
                    </div>
                  </div>

                  <div className="brands-grid">
                    {brands.map((brand, index) => (
                      <div
                        className="brand-card"
                        key={`${brand.name}-${index}`}
                      >
                        <div className="brand-logo">
                          {brandImages[brand.name] ? (
                            <img
                              src={brandImages[brand.name]}
                              alt={brand.name}
                            />
                          ) : (
                            brand.name?.charAt(0)?.toUpperCase()
                          )}
                        </div>

                        <div className="brand-content">
                          <div className="brand-name">{brand.name}</div>

                          {brand.phoneNumber && (
                            <a href={`tel:${brand.phoneNumber}`}>
                              <Phone size={14} />
                              {brand.phoneNumber}
                            </a>
                          )}

                          {brand.emailAddress && (
                            <a href={`mailto:${brand.emailAddress}`}>
                              <Mail size={14} />
                              {brand.emailAddress}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* SIDEBAR */}
            <aside className="store-sidebar">
              {Number.isFinite(latitude) && Number.isFinite(longitude) && (
                <section className="map-card">
                  <div className="map-header">
                    <div className="contact-heading">
                      <div className="contact-icon">
                        <MapPin size={21} />
                      </div>

                      <div>
                        <div className="contact-title">Find us</div>

                        <div className="contact-subtitle">{fullAddress}</div>
                      </div>
                    </div>
                    {/* <MapPin size={22} /> */}
                  </div>

                  <div className="map-wrapper">
                    <iframe
                      title={`${store.name} location`}
                      src={`https://www.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>

                  {mapsUrl && (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="map-directions"
                    >
                      <Navigation size={17} />
                      Get Direction
                    </a>
                  )}
                </section>
              )}

              {/* CONTACT */}
              <section className="contact-card">
                <div className="contact-heading">
                  <div className="contact-icon">
                    <Phone size={21} />
                  </div>

                  <div>
                    <div className="contact-title">Contact</div>

                    <div className="contact-subtitle">
                      Get in touch with the store
                    </div>
                  </div>
                </div>

                {store.Contact?.name && (
                  <div className="contact-item">
                    <div className="contact-label">Contact</div>

                    <div className="contact-value">{store.Contact.name}</div>
                  </div>
                )}

                {(store.Contact?.phoneNumber || address.phoneNumber) && (
                  <a
                    className="contact-link"
                    href={`tel:${
                      store.Contact?.phoneNumber || address.phoneNumber
                    }`}
                  >
                    <Phone size={17} />

                    <div>
                      <div className="contact-label">Phone</div>

                      <div className="contact-value">
                        {store.Contact?.phoneNumber || address.phoneNumber}
                      </div>
                    </div>
                  </a>
                )}

                {store.Contact?.emailAddress && (
                  <a
                    className="contact-link"
                    href={`mailto:${store.Contact.emailAddress}`}
                  >
                    <Mail size={17} />

                    <div>
                      <div className="contact-label">Email</div>

                      <div className="contact-value">
                        {store.Contact.emailAddress}
                      </div>
                    </div>
                  </a>
                )}
              </section>

              {/* ADDRESS */}
              {/* <section className="address-card">
                <div className="address-heading">
                  <MapPin size={21} />

                  <div className="address-title">Store address</div>
                </div>

                <div className="address-text">{fullAddress}</div>

                {mapsUrl && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="address-button"
                  >
                    <Navigation size={17} />
                    Get directions
                  </a>
                )}
              </section> */}
            </aside>
          </div>
        </main>
      </div>
    </>
  );
}
